import { useQuery } from "@tanstack/react-query";
import { attendanceService } from "../../../api/attendanceService";
import Badge from "../../../components/ui/Badge";
import Modal from "../../../components/ui/Modal";
import { getErrorMessage } from "../../../utils/apiError";
import { formatDate } from "../../../utils/date";
import { formatDistance } from "../../../utils/geolocation";
import { cx } from "../../../utils/classNames";
import SessionTimeline from "./SessionTimeline";
import ui from "../../../components/ui/ui.module.css";
import styles from "../Sessions.module.css";

// Check-in / check-out point of one session.
const PointCard = ({ title, point, hasTime }) => {
  if (!hasTime) return null;
  const hasLocation = point?.latitude != null && point?.longitude != null;

  return (
    <div
      className={cx(styles.point, point?.outsideBranch && styles.pointOutside)}
    >
      <div className={styles.pointTitle}>
        <span>{title}</span>
        {point?.outsideBranch === true && (
          <Badge tone="amber" icon="bi-geo-alt-fill">
            Outside branch
          </Badge>
        )}
      </div>

      {hasLocation ? (
        <>
          <div>
            {point.distance != null
              ? `${formatDistance(point.distance)} from branch`
              : "Distance not checked"}
            {point.accuracy != null && ` · ±${formatDistance(point.accuracy)}`}
          </div>
          {point.mapUrl && (
            <a href={point.mapUrl} target="_blank" rel="noopener noreferrer">
              <i className="bi bi-map me-1"></i>
              Open in Maps
            </a>
          )}
        </>
      ) : (
        <div>No location recorded</div>
      )}
    </div>
  );
};

// Where each check-in / check-out of a day happened (GET /attendance/:id/locations).
const LocationsModal = ({ attendanceId, onClose }) => {
  const query = useQuery({
    queryKey: ["me:attendance", "locations", attendanceId],
    queryFn: () => attendanceService.getLocations(attendanceId),
  });

  const data = query.data;
  const branch = data?.branch;

  return (
    <Modal
      title={data ? `Locations · ${formatDate(data.date)}` : "Locations"}
      onClose={onClose}
    >
      {query.isLoading ? (
        <div className={ui.empty}>
          <span
            className="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          ></span>
          Loading locations…
        </div>
      ) : query.isError ? (
        <div className={cx(ui.alert, ui.alertDanger)}>
          {getErrorMessage(
            query.error,
            "Unable to load the locations for this day.",
          )}
        </div>
      ) : (
        <div className={styles.panelStack}>
          {branch ? (
            <div className={styles.branchRow}>
              <i className="bi bi-building"></i>
              <span className={styles.branchName}>{branch.name}</span>
              {branch.radius != null ? (
                <span>Allowed radius {formatDistance(branch.radius)}</span>
              ) : (
                <span>No branch location set</span>
              )}
              {branch.mapUrl && (
                <a
                  href={branch.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open in Maps
                </a>
              )}
            </div>
          ) : (
            <div className={cx(ui.alert, ui.alertGray)}>
              No branch assigned.
            </div>
          )}

          {data.sessions.length ? (
            <SessionTimeline
              sessions={data.sessions}
              renderExtra={(session) => (
                <div className={styles.points}>
                  <PointCard
                    title="Check in"
                    point={session.checkIn}
                    hasTime={!!session.checkInTime}
                  />
                  <PointCard
                    title="Check out"
                    point={session.checkOut}
                    hasTime={!!session.checkOutTime}
                  />
                </div>
              )}
            />
          ) : (
            <div className={ui.empty}>No check-ins on this day.</div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default LocationsModal;
